import { useEffect } from 'react'
import { takePendingResend } from '@/store/aiAccessStore'

interface ChatMessage {
  role: string
  content: string
}

/**
 * Carry on with the turn an out-of-credits refusal stopped, once the user has
 * fixed it (bought credits, added their own key, or switched to a free model).
 *
 * The refusal arrived over the agent socket, so there is no request to replay —
 * and when the fix switched AI engines the dock that raised it has since been
 * replaced by another. So the AiAccessModal leaves a one-shot `pendingResend`
 * and whichever chat dock mounts next for the project sends it. Call this
 * AFTER the hook's transcript-restore effect, so the restored conversation is
 * in place before the refused turn is trimmed from it and sent again.
 */
export function useResendAfterAccess<M extends ChatMessage>({
  project, userId, startTask, setMessages,
}: {
  project: string
  userId: string
  startTask: (taskType: string, taskData: Record<string, unknown>) => Promise<void>
  setMessages: (fn: (prev: M[]) => M[]) => void
}) {
  useEffect(() => {
    if (!userId) return
    const pending = takePendingResend(project, ['task'])
    if (!pending) return
    const description = String(
      pending.taskData.description || pending.taskData.content || pending.taskType)
    // The refused turn is still at the end of the transcript (the user's
    // message + an empty assistant bubble); drop it so the resend doesn't show
    // the same message twice.
    setMessages((prev) => {
      const out = [...prev]
      const last = out[out.length - 1]
      if (last && last.role === 'assistant' && !last.content.trim()) out.pop()
      const user = out[out.length - 1]
      if (user && user.role === 'user' && user.content === description) out.pop()
      return out
    })
    void startTask(pending.taskType, pending.taskData)
  }, [project, userId, startTask, setMessages])
}
